import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnInit } from '@angular/core';
import * as _ from 'lodash';
import { BehaviorSubject, combineLatest, take } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { finalize, map, share, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments';
import { SubService } from '../../components';
import { PrismHeaders, ServiceStatusEnums, ServiceStatusesSnakeCaseEnums, ServiceStatusSnakeCaseTypes } from '../../enums';
import { Response, Service } from '../../models';
import { ServiceResponse } from '../../models/http-response/service-reponse';

@Injectable()
export class ServicesService implements OnInit {
  private downloadData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private selectedServiceBS: BehaviorSubject<Service> = new BehaviorSubject<Service>(null);
  private isGetServicesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private isGetConfirmationsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private isPostServicesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private isPutServicesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private isGetSubServicesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private servicesBSubject: BehaviorSubject<Service[]> = new BehaviorSubject<Service[]>(null);
  private servicesCountBSubject: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  private notInvoicedCounterBSubject: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  private includeLuxEngineCounterBSubject: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  public downloadProgress = {};
  private _subServices$: BehaviorSubject<SubService[]> = new BehaviorSubject<SubService[]>(null);

  constructor(private http: HttpClient) {
  }

  ngOnInit() {
  }

  get services$() {
    return this.servicesBSubject.asObservable();
  }

  get serviceCount$() {
    return this.servicesCountBSubject.asObservable();
  }

  get serviceCount() {
    return this.servicesCountBSubject.getValue();
  }

  get notInvoicedCounter$() {
    return this.notInvoicedCounterBSubject.asObservable();
  }

  get notInvoicedCounter() {
    return this.notInvoicedCounterBSubject.getValue();
  }

  get includeLuxEngineCounter() {
    return this.includeLuxEngineCounterBSubject.getValue();
  }

  get selectedService() {
    return this.selectedServiceBS.value;
  }

  get selectedService$() {
    return this.selectedServiceBS.asObservable()
      .pipe(
        share()
      );
  }

  setSelectedService(service: Service) {
    this.selectedServiceBS.next(service);
  }

  clearNotification(service: Service, notification: 'newRaws' | 'other') {
    const headers = new HttpHeaders()
      .append(PrismHeaders.QueryData, JSON.stringify({pathToJob: service.pathToProcessorService, notification}));

    return this.http.get(`${environment.apiBaseUrl}/processor2-clear-job-notification`, {headers});
  }

  getServices({pageIndex, pageSize, notInvoiced, includeLuxEngine}: {pageIndex: number, pageSize: number, notInvoiced?: boolean, includeLuxEngine?: boolean}) {
    const headers = new HttpHeaders()
      .append(PrismHeaders.QueryData, JSON.stringify({pageIndex, pageSize, notInvoiced, includeLuxEngine}));

    this.switchGetServicesLoaderOn();

    return this.http.get(environment.apiBaseUrl + '/processor2-get-services', {headers})
      .pipe(
        tap((services: ServiceResponse) => {
          this.servicesBSubject.next(services.data);
        }),
        shareReplay(1),
        finalize(() => {
          this.switchGetServicesLoaderOff();
        })
      );
  }

  getServicesCount(notInvoiced?: boolean, includeLuxEngine?: boolean) {
    const headers = new HttpHeaders()
      .append(PrismHeaders.QueryData, JSON.stringify({notInvoiced, includeLuxEngine}));

    return this.http.get(environment.apiBaseUrl + '/processor2-get-services-count', {headers})
      .pipe(
        take(1)
      )
      .subscribe((res: any) => {
        this.servicesCountBSubject.next(res.data.counter);
        this.notInvoicedCounterBSubject.next(res.data.notInvoicedCounter);
        this.includeLuxEngineCounterBSubject.next(res.data.includeLuxEngineCounter);
        this.switchGetServicesLoaderOff();
      });
  }

  postConfirmation(service: Service, confirmations: SubServiceConfirmation) {
    return this.http.post(environment.apiBaseUrl + '/processor2-post-confirmation', {service, confirmations});
  }

  getConfirmations(serviceId: string) {
    this.switchGetConfirmationsLoaderOn()
    const headers = new HttpHeaders()
      .append(PrismHeaders.QueryData, JSON.stringify({id: serviceId}));
    return this.http.get(environment.apiBaseUrl + '/processor2-get-confirmations', {headers})
      .pipe(
        map((res: Response) => res.data)
      );
  }

  clearDownloadData() {
    this.downloadData.next(null);
  }

  get isGetServicesLoading$() {
    return this.isGetServicesLoading.asObservable();
  }

  get isPostServicesLoading$() {
    return this.isPostServicesLoading.asObservable();
  }

  get isPutServicesLoading$() {
    return this.isPutServicesLoading.asObservable();
  }

  get areServicesLoading$() {
    return combineLatest(this.isGetServicesLoading$, this.isPostServicesLoading$, this.isPutServicesLoading$)
      .pipe(
        map(x => _.includes(x, true))
      );
  }

  switchGetServicesLoaderOn() {
    this.isGetServicesLoading.next(true);
  }

  switchGetServicesLoaderOff() {
    this.isGetServicesLoading.next(false);
  }

  get isGetSubServiceLoading$() {
    return this.isGetSubServicesLoading.asObservable();
  }

  switchGetConfirmationsLoaderOn() {
    this.isGetConfirmationsLoading.next(true);
  }

  switchGetConfirmationsLoaderOff() {
    this.isGetConfirmationsLoading.next(false);
  }

  get isGetConfirmationsLoading$() {
    return this.isGetConfirmationsLoading.asObservable()
  }

  switchGetSubServicesLoaderOn() {
    this.isGetSubServicesLoading.next(true);
  }

  switchGetSubServicesLoaderOff() {
    this.isGetSubServicesLoading.next(false);
  }

  getSearchedServiceDetails({searchTags}) {
    const headers = new HttpHeaders()
      .append(PrismHeaders.QueryData, JSON.stringify({searchTags}));

    return this.http.get(`${environment.apiBaseUrl}/processor2-get-services-details`, {headers});
  }

  getServicesDetails({pageSize, pageIndex}): Observable<any> {
    const headers = new HttpHeaders()
      .append(PrismHeaders.QueryData, JSON.stringify({pageSize, pageIndex}));

    return this.http.get(`${environment.apiBaseUrl}/processor2-get-services-details`, {headers});
  }

  getServicesFinals({finalDirectories}): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/processor2-get-services-finals`, {finalDirectories});
  }

  getSubServices() {
    this.switchGetSubServicesLoaderOn();
    return this.http.get(`${environment.apiBaseUrl}/processor2-get-sub-services`).pipe(
      take(1),
      map((res: Response) => res.data),
      tap((subServices: SubService[]) => {
        this.updateSubServices(subServices);
      }),
      finalize(() => {
        this.switchGetSubServicesLoaderOff();
      })
    );
  }

  updateSubServices(subServices: SubService[]) {
    this._subServices$.next(subServices);
  }

  updateServiceStatus(id, status: ServiceStatusSnakeCaseTypes) {
    const services = this.servicesBSubject.getValue();
    const index = services.findIndex((service) => {
      return id === service.id;
    });
    if (index < 0) {
      return;
    }

    switch (status) {
      case ServiceStatusesSnakeCaseEnums.memberReview:
        services[index].status = ServiceStatusEnums.memberReview;
        services[index].statusSnakeCase = ServiceStatusesSnakeCaseEnums.memberReview;
        break;
      case ServiceStatusesSnakeCaseEnums.inProgress:
        services[index].status = ServiceStatusEnums.inProgress;
        services[index].statusSnakeCase = ServiceStatusesSnakeCaseEnums.inProgress;
        break;
      case ServiceStatusesSnakeCaseEnums.completed:
        services[index].status = ServiceStatusEnums.completed;
        services[index].statusSnakeCase = ServiceStatusesSnakeCaseEnums.completed;
        break;
    }

    this.servicesBSubject.next(services);
  }
}

interface SubServiceConfirmation {
  [id: string]: {
    itemCode: string;
    itemCount: number;
  };
}

